/**
 * Cookie Consent & Compliance Service
 * Diamond School of Nursing - Study Hub
 * 
 * Supports:
 * - GDPR (EU General Data Protection Regulation - Art. 6, 7 & Recital 32)
 * - ePrivacy Directive (Directive 2002/58/EC on Privacy and Electronic Communications)
 * - CCPA / CPRA (California Consumer Privacy Act / California Privacy Rights Act § 1798.120)
 * - US State Privacy Frameworks (VCDPA, CPA, CTDPA, UCPA)
 */

export interface CookiePreferences {
  status: 'accepted' | 'declined' | 'customized';
  necessary: boolean;
  analytics: boolean;
  functional: boolean;
  marketing: boolean;
  doNotSell: boolean;
  updatedAt: string;
  version: string;
}

export interface CookieItem {
  name: string;
  provider: string;
  purpose: string;
  duration: string;
  type: '1st Party' | '3rd Party';
  category: 'necessary' | 'analytics' | 'functional' | 'marketing';
}

const STORAGE_KEY = 'cna_cookie_consent';
const CONSENT_VERSION = '2026.1';

export const COOKIE_INVENTORY: CookieItem[] = [
  // Strictly Necessary
  {
    name: 'wix_auth_token',
    provider: 'Diamond School of Nursing / Wix SSO',
    purpose: 'Maintains authenticated student session and verifies enrolled nursing credentials.',
    duration: 'Session / 30 Days',
    type: '1st Party',
    category: 'necessary'
  },
  {
    name: 'dns_session_token',
    provider: 'Diamond School of Nursing',
    purpose: 'Secures API communication between client browser and clinical database engine.',
    duration: '30 Days',
    type: '1st Party',
    category: 'necessary'
  },
  {
    name: 'cna_cookie_consent',
    provider: 'Diamond School of Nursing',
    purpose: 'Stores your granular cookie consent choices and compliance timestamp.',
    duration: '12 Months',
    type: '1st Party',
    category: 'necessary'
  },
  {
    name: 'active_cert_id',
    provider: 'Diamond School of Nursing',
    purpose: 'Remembers your active study specialty (CNA, Phlebotomy, CMA, Pharmacy Tech, PCT).',
    duration: '12 Months',
    type: '1st Party',
    category: 'necessary'
  },
  {
    name: 'dns_manage_mode',
    provider: 'Diamond School of Nursing',
    purpose: 'Authorizes administrative faculty members to access clinical management features.',
    duration: 'Session',
    type: '1st Party',
    category: 'necessary'
  },

  // Analytics & Performance
  {
    name: '_ga',
    provider: 'Google Analytics (GA4)',
    purpose: 'Calculates visitor, session, and campaign data to monitor Study Hub availability.',
    duration: '2 Years',
    type: '3rd Party',
    category: 'analytics'
  },
  {
    name: '_ga_*',
    provider: 'Google Analytics (GA4)',
    purpose: 'Used by Google Analytics to persist session state across interactive quiz tabs.',
    duration: '2 Years',
    type: '3rd Party',
    category: 'analytics'
  },
  {
    name: 'cna_speed_metric',
    provider: 'Diamond School of Nursing',
    purpose: 'Measures question prompt latency and image render speeds to prevent clinical exam lag.',
    duration: 'Session',
    type: '1st Party',
    category: 'analytics'
  },

  // Functional & Personalization
  {
    name: 'dns_selected_state',
    provider: 'Diamond School of Nursing',
    purpose: 'Retains your state testing jurisdiction (e.g. California, Florida, Texas, New York).',
    duration: '12 Months',
    type: '1st Party',
    category: 'functional'
  },
  {
    name: 'data-theme',
    provider: 'Diamond School of Nursing',
    purpose: 'Remembers your active learning interface theme (Clinical Trust, Warm Academic, etc.).',
    duration: '12 Months',
    type: '1st Party',
    category: 'functional'
  },
  {
    name: 'audio_playback_rate',
    provider: 'Diamond School of Nursing',
    purpose: 'Persists your preferred audio podcast playback speed multiplier (1x, 1.25x, 1.5x).',
    duration: '12 Months',
    type: '1st Party',
    category: 'functional'
  },
  {
    name: 'fontSize',
    provider: 'Diamond School of Nursing',
    purpose: 'Preserves font-size accessibility adjustments for comfortable clinical reading.',
    duration: '12 Months',
    type: '1st Party',
    category: 'functional'
  },

  // Marketing & Advertising
  {
    name: '_fbp',
    provider: 'Meta Platforms (Facebook Pixel)',
    purpose: 'Measures advertisement conversions and delivers relevant clinical education promos.',
    duration: '90 Days',
    type: '3rd Party',
    category: 'marketing'
  },
  {
    name: '__gads / __gpi',
    provider: 'Google AdSense (ca-pub-6693032236212368)',
    purpose: 'Provides frequency capping and non-fraudulent ad distribution for nursing gear.',
    duration: '13 Months',
    type: '3rd Party',
    category: 'marketing'
  }
];

export const CookieConsentService = {
  getPreferences(): CookiePreferences {
    if (typeof window === 'undefined') {
      return this.getDefaults('declined');
    }

    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return this.getDefaults('declined');
      }

      // Legacy string format backward compatibility
      if (raw === 'accepted') {
        return {
          status: 'accepted',
          necessary: true,
          analytics: true,
          functional: true,
          marketing: true,
          doNotSell: false,
          updatedAt: new Date().toISOString(),
          version: CONSENT_VERSION
        };
      }

      if (raw === 'declined') {
        return {
          status: 'declined',
          necessary: true,
          analytics: false,
          functional: false,
          marketing: false,
          doNotSell: true,
          updatedAt: new Date().toISOString(),
          version: CONSENT_VERSION
        };
      }

      const parsed = JSON.parse(raw);
      return {
        status: parsed.status || 'customized',
        necessary: true, // Always true
        analytics: Boolean(parsed.analytics),
        functional: Boolean(parsed.functional),
        marketing: Boolean(parsed.marketing),
        doNotSell: Boolean(parsed.doNotSell),
        updatedAt: parsed.updatedAt || new Date().toISOString(),
        version: parsed.version || CONSENT_VERSION
      };
    } catch (e) {
      return this.getDefaults('declined');
    }
  },

  hasUserDecided(): boolean {
    if (typeof window === 'undefined') return false;
    const raw = localStorage.getItem(STORAGE_KEY);
    return Boolean(raw);
  },

  getDefaults(status: 'accepted' | 'declined' | 'customized' = 'declined'): CookiePreferences {
    const isAll = status === 'accepted';
    return {
      status,
      necessary: true,
      analytics: isAll,
      functional: isAll,
      marketing: isAll,
      doNotSell: !isAll,
      updatedAt: new Date().toISOString(),
      version: CONSENT_VERSION
    };
  },

  savePreferences(prefs: Partial<CookiePreferences>): CookiePreferences {
    const current = this.getPreferences();
    const updated: CookiePreferences = {
      ...current,
      ...prefs,
      necessary: true, // Non-negotiable
      updatedAt: new Date().toISOString(),
      version: CONSENT_VERSION
    };

    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      } catch (err) {
        console.warn('Failed to save cookie preferences to localStorage:', err);
      }

      // Dispatch legacy string detail for backward compatibility with AnalyticsTracker
      const legacyDetail = updated.analytics ? 'accepted' : 'declined';
      window.dispatchEvent(new CustomEvent('cookie-consent-updated', { 
        detail: legacyDetail 
      }));

      // Dispatch granular rich detail event
      window.dispatchEvent(new CustomEvent('cookie-consent-granular-updated', { 
        detail: updated 
      }));
    }

    return updated;
  },

  acceptAll(): CookiePreferences {
    return this.savePreferences({
      status: 'accepted',
      analytics: true,
      functional: true,
      marketing: true,
      doNotSell: false
    });
  },

  rejectNonEssential(): CookiePreferences {
    return this.savePreferences({
      status: 'declined',
      analytics: false,
      functional: false,
      marketing: false,
      doNotSell: true
    });
  },

  setDoNotSell(optOut: boolean): CookiePreferences {
    const current = this.getPreferences();
    return this.savePreferences({
      doNotSell: optOut,
      // If user opts out of sale/sharing under CCPA/CPRA, marketing trackers are disabled
      marketing: optOut ? false : current.marketing
    });
  },

  openPreferencesModal(): void {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('open-cookie-preferences'));
    }
  },

  getInventoryByCategory(category: 'necessary' | 'analytics' | 'functional' | 'marketing'): CookieItem[] {
    return COOKIE_INVENTORY.filter(c => c.category === category);
  }
};
